import React, { useEffect, useRef, useState } from 'react';
import L, { divIcon, DomEvent, LatLngTuple, LeafletMouseEvent, map, marker, Marker, MarkerOptions, point, tileLayer } from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { formatCoords, formatDate } from 'Compass/profile-lab/utils/assetFormatting';
import { getSelectionSets } from 'Compass/profile-lab/utils/selectionUtils';
import { fetchSignedUrlsForCurrentUser } from 'Compass/profile-lab/hooks/useSignedUrls';
import { ErrorMessage, LoadingMessage } from 'Compass/profile-lab/styles/common';
import { MapContainer, Overlay, OverlayCloseButton, OverlayContent, OverlayImage, OverlayMetadata } from 'Compass/profile-lab/styles/map';

interface MarkerData {
  lat: number;
  lng: number;
  photoLid: number;
  status: 'selected' | 'grow' | 'none';
  metadata: string[];
}

interface LeafletMarkerOptions extends MarkerOptions {
  status: 'selected' | 'grow' | 'none';
}

const ProfileLabMapPage: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const [markers, setMarkers] = useState<MarkerData[]>([]);
  const signedUrlsRef = useRef<Record<number, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [overlayImage, setOverlayImage] = useState('');
  const [overlayMetadata, setOverlayMetadata] = useState<string[]>([]);
  const [overlayStatus, setOverlayStatus] = useState<string>('');
  const mapInstanceRef = useRef<L.Map | null>(null);

  useEffect(() => {
    const fetchMarkers = async () => {
      setLoading(true);
      setError(null);
      try {
        const { db } = await import('../services/db');
        await db.init();

        const allPhotos = await db.getAllPhotos();
        const { selectedSet, growSet } = await getSelectionSets();

        const invalid_coord = -180;
        const markersData: MarkerData[] = [];

        for (const asset of allPhotos) {
          if (asset.latitude != null && asset.longitude != null &&
              (asset.latitude !== 0 || asset.longitude !== 0) &&
              !(asset.latitude === invalid_coord && asset.longitude === invalid_coord)) {

            const dateStr = formatDate(asset.photo_date);
            const coords = formatCoords(asset.latitude, asset.longitude);

            const status = selectedSet.has(asset.photo_lid) ? 'selected' :
                          growSet.has(asset.photo_lid) ? 'grow' :
                          'none';

            markersData.push({
              lat: asset.latitude,
              lng: asset.longitude,
              photoLid: asset.photo_lid,
              status,
              metadata: [dateStr, coords]
            });
          }
        }

        setMarkers(markersData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load map data';
        setError(errorMessage);
        console.error('Failed to fetch map data:', err);
      } finally {
        setLoading(false);
      }
    };

    void fetchMarkers();
  }, []);

  useEffect(() => {
    const showOverlay = async (photoLid: number, metadata: string[], status: string) => {
      try {
        let url = signedUrlsRef.current[photoLid];

        if (!url) {
          const urls = await fetchSignedUrlsForCurrentUser([photoLid]);
          url = urls[photoLid];

          if (url) {
            signedUrlsRef.current[photoLid] = url;
          }
        }

        if (url) {
          setOverlayImage(url);
          setOverlayMetadata(metadata);
          setOverlayStatus(status);
          setOverlayVisible(true);
        }
      } catch (error) {
        console.error('Failed to load image for overlay:', error);
      }
    };

    if (!mapRef.current || markers.length === 0) return;

    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
    }

    const mapInstance = map(mapRef.current).setView([0, 0], 2);
    mapInstanceRef.current = mapInstance;

    tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(mapInstance);

    const bounds: LatLngTuple[] = [];
    const markerCluster = L.markerClusterGroup({
      iconCreateFunction: (cluster: L.MarkerCluster) => {
        const childMarkers = cluster.getAllChildMarkers();
        let selectedCount = 0;
        let growCount = 0;
        const totalCount = childMarkers.length;

        childMarkers.forEach((m: Marker) => {
          const options = m.options as LeafletMarkerOptions;
          if (options.status === 'selected') {
            selectedCount++;
          } else if (options.status === 'grow') {
            growCount++;
          }
        });

        let r, g, b;
        if (growCount === 0 && selectedCount === 0) {
          r = 255;
          g = 165;
          b = 79;
        } else {
          const baseR = 150,
            baseG = 150,
            baseB = 150;
          const combinedRatio = (growCount + selectedCount) / totalCount;

          if (growCount > 0) {
            const targetR = 0,
              targetG = 204,
              targetB = 0;
            r = Math.round(baseR + (targetR - baseR) * combinedRatio);
            g = Math.round(baseG + (targetG - baseG) * combinedRatio);
            b = Math.round(baseB + (targetB - baseB) * combinedRatio);
          } else {
            const targetR = 204,
              targetG = 0,
              targetB = 204;
            r = Math.round(baseR + (targetR - baseR) * combinedRatio);
            g = Math.round(baseG + (targetG - baseG) * combinedRatio);
            b = Math.round(baseB + (targetB - baseB) * combinedRatio);
          }
        }

        let className = 'marker-cluster marker-cluster-small';
        if (totalCount >= 100) className = 'marker-cluster marker-cluster-large';
        else if (totalCount >= 10) className = 'marker-cluster marker-cluster-medium';

        return divIcon({
          html: `<div style="background-color: rgba(${r},${g},${b},0.8);"><span>${totalCount}</span></div>`,
          className: className,
          iconSize: point(40, 40),
        });
      },
    });

    markers.forEach((markerData) => {
      let markerColor;
      if (markerData.status === 'selected') {
        markerColor = 'rgba(255, 0, 255, 0.7)';
      } else if (markerData.status === 'grow') {
        markerColor = 'rgba(0, 255, 0, 0.7)';
      } else {
        markerColor = 'rgba(0, 123, 255, 0.7)';
      }

      const icon = divIcon({
        className: 'custom-marker',
        html: `<div style="background-color: ${markerColor}; width: 10px; height: 10px; border-radius: 50%; border: 2px solid white;"></div>`,
        iconSize: [10, 10],
      });

      const m = marker([markerData.lat, markerData.lng], {
        icon,
        status: markerData.status,
      } as LeafletMarkerOptions);

      m.on('click', (e: LeafletMouseEvent) => {
        DomEvent.stopPropagation(e);
        void showOverlay(markerData.photoLid, markerData.metadata, markerData.status);
      });
      markerCluster.addLayer(m);
      bounds.push([markerData.lat, markerData.lng]);
    });

    mapInstance.addLayer(markerCluster);

    if (bounds.length > 0) {
      mapInstance.fitBounds(bounds, { padding: [50, 50] });
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, [markers]);

  const closeOverlay = () => {
    setOverlayVisible(false);
  };

  if (loading) {
    return <LoadingMessage>Loading map...</LoadingMessage>;
  }

  if (error) {
    return <ErrorMessage>Error: {error}</ErrorMessage>;
  }

  return (
    <>
      <MapContainer ref={mapRef} />

      <Overlay $visible={overlayVisible} onClick={closeOverlay}>
        <OverlayCloseButton>&times;</OverlayCloseButton>
        <OverlayContent>
          <OverlayImage src={overlayImage} alt="Selected" />
          <OverlayMetadata $status={overlayStatus}>
            {overlayMetadata.map((line, idx) => (
              <div key={idx}>{line}</div>
            ))}
          </OverlayMetadata>
        </OverlayContent>
      </Overlay>
    </>
  );
};

export default ProfileLabMapPage;
